import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Phone, Mail, ChevronLeft, ChevronRight, Pencil, Trash2, X } from 'lucide-react';
import { getFaultReports, getFaultReportAttempts, patchFaultReport, deleteFaultReport } from '../../../redux/api';
import HeaderSection from '../../header/HeaderSection';

const APPLIANCE_LABELS = {
  ves_masina: 'Veš mašina',
  susilica: 'Sušilica',
  frizider: 'Frižider',
  zamrzivac: 'Zamrzivač',
  sudomasina: 'Sudomašina',
  sporet: 'Šporet / rerna',
  mikrotalasna: 'Mikrotalasna',
  bojler: 'Bojler',
  klima: 'Klima',
  aspirator: 'Aspirator',
  grejalica: 'Grejalica',
  usisivac: 'Usisivač',
  ostalo: 'Ostalo'
};

const STATUS_LABELS = {
  new: 'Nova',
  contacted: 'Odgovoreno',
  scheduled: 'Zakazano',
  done: 'Popravka urađena',
  cancelled: 'Otkazano'
};

const emptyEdit = {
  name: '',
  email: '',
  phone: '',
  address: '',
  appliance: 'ostalo',
  machineModel: '',
  description: '',
  notes: '',
  forwardedTo: '',
  quoteAmount: ''
};

const formatWhen = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('sr-RS', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const offerDraft = (report) => {
  const appliance = APPLIANCE_LABELS[report.appliance] || report.appliance || 'aparat';
  const subject = `Ponuda za popravku — ${appliance}`;
  const greeting = report.name ? `Poštovani/a ${report.name},` : 'Poštovani/a,';
  const body = [
    greeting,
    '',
    `Hvala na prijavi kvara (${appliance}).`,
    report.address ? `Adresa: ${report.address}` : '',
    report.description ? `Opis: ${report.description}` : '',
    '',
    'Orijentaciona cena: _____ RSD',
    'Termin: u toku dana',
    '',
    'Ako Vam odgovara, javite se da dogovorimo dolazak.',
    '',
    'Srdačan pozdrav'
  ]
    .filter((line, index, arr) => line !== '' || arr[index - 1] !== '')
    .join('\n');
  const params = new URLSearchParams({
    view: 'cm',
    fs: '1',
    to: report.email || '',
    su: subject,
    body
  });
  return {
    subject,
    body,
    mailto: `mailto:${report.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
    gmail: `https://mail.google.com/mail/?${params.toString()}`
  };
};

const FaultReportsView = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [drafts, setDrafts] = useState([]);
  const [draftsError, setDraftsError] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyEdit);
  const [saving, setSaving] = useState(false);

  const load = async (nextPage = page) => {
    try {
      setLoading(true);
      const params = {};
      if (status) params.status = status;
      const reportsRes = await getFaultReports(nextPage, 20, params);
      setReports(reportsRes.data.reports || []);
      setTotal(reportsRes.data.total || 0);
      setTotalPages(reportsRes.data.totalPages || 1);
      setPage(reportsRes.data.page || nextPage);

      if (Array.isArray(reportsRes.data.unfinishedLeads)) {
        setDrafts(reportsRes.data.unfinishedLeads);
        setDraftsError(false);
      } else {
        try {
          const draftsRes = await getFaultReportAttempts();
          setDrafts(draftsRes.data?.attempts || []);
          setDraftsError(false);
        } catch {
          setDrafts([]);
          setDraftsError(true);
        }
      }
    } catch (error) {
      console.error(error);
      toast.error('Greška pri učitavanju prijava');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const applyReport = (updated) => {
    setReports((prev) => prev.map((item) => (item._id === updated._id ? updated : item)));
  };

  const patch = async (id, data) => {
    const response = await patchFaultReport(id, data);
    const updated = response.data.report;
    if (updated) applyReport(updated);
    return updated;
  };

  const changeStatus = async (id, nextStatus) => {
    try {
      await patch(id, { status: nextStatus });
    } catch {
      toast.error('Status nije sačuvan');
    }
  };

  const toggleFlag = async (report, field, value) => {
    try {
      const data = { [field]: value };
      if (field === 'offerSent' && value && report.status === 'new') data.status = 'contacted';
      await patch(report._id, data);
    } catch {
      toast.error('Nije sačuvano');
    }
  };

  const toggleRepaired = async (report, repaired) => {
    try {
      await patch(report._id, { status: repaired ? 'done' : 'scheduled' });
    } catch {
      toast.error('Status nije sačuvan');
    }
  };

  const openEdit = (report) => {
    setEditingId(report._id);
    setEditForm({
      name: report.name || '',
      email: report.email || '',
      phone: report.phone || '',
      address: report.address || '',
      appliance: report.appliance || 'ostalo',
      machineModel: report.machineModel || '',
      description: report.description || '',
      notes: report.notes || '',
      forwardedTo: report.forwardedTo || '',
      quoteAmount: report.quoteAmount || ''
    });
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    if (!editingId) return;
    setSaving(true);
    try {
      await patch(editingId, editForm);
      setEditingId(null);
      toast.success('Prijava je sačuvana');
    } catch {
      toast.error('Izmena nije uspela');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (report) => {
    if (!window.confirm(`Obrisati prijavu${report.name ? ` (${report.name})` : ''}?`)) return;
    try {
      await deleteFaultReport(report._id);
      setReports((prev) => prev.filter((item) => item._id !== report._id));
      setTotal((n) => Math.max(0, n - 1));
      if (editingId === report._id) setEditingId(null);
      toast.success('Prijava je obrisana');
    } catch {
      toast.error('Brisanje nije uspelo');
    }
  };

  const markOfferSent = async (report) => {
    if (report.offerSent) return;
    try {
      await patch(report._id, {
        offerSent: true,
        ...(report.status === 'new' ? { status: 'contacted' } : {})
      });
    } catch {
      // mailto still opens
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <HeaderSection title="Prijave kvarova" onRead={() => load(page)} />
      <p className="text-sm text-gray-600 mb-4">
        Forme sa spintasker.com/objavi-kvar. Nisu vidljive serviserima u aplikaciji.
      </p>

      <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
        <h2 className="font-semibold text-amber-950 mb-1">
          Uneli podatke, nisu poslali prijavu ({drafts.length})
        </h2>
        <p className="text-xs text-amber-800 mb-3">
          Mejl ili telefon se čuvaju čim ih kucaju na objavi-kvar, i ako ne pošalju formu.
        </p>
        {draftsError ? (
          <p className="text-sm text-red-700">
            API još nema ovaj log. Deployuj tasker, pa osveži ovu stranu.
          </p>
        ) : drafts.length === 0 ? (
          <p className="text-sm text-amber-900">
            Još nema unosa. Kad neko ukuca mejl ili telefon na formi i ne pošalje, pojaviće se ovde.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-amber-900/70 border-b border-amber-200">
                  <th className="py-2 pr-3">Mejl</th>
                  <th className="py-2 pr-3">Telefon</th>
                  <th className="py-2 pr-3">Ime</th>
                  <th className="py-2 pr-3">Adresa</th>
                  <th className="py-2">Poslednji unos</th>
                </tr>
              </thead>
              <tbody>
                {drafts.map((row) => (
                  <tr key={row._id} className="border-b border-amber-100">
                    <td className="py-2 pr-3 text-gray-900">
                      {row.email ? (
                        <a href={`mailto:${row.email}`} className="text-blue-700 hover:underline">
                          {row.email}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-2 pr-3 text-gray-900">
                      {row.phone ? (
                        <a href={`tel:${row.phone}`} className="text-blue-700 hover:underline">
                          {row.phone}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-2 pr-3 text-gray-700">{row.name || '—'}</td>
                    <td className="py-2 pr-3 text-gray-700">{row.address || '—'}</td>
                    <td className="py-2 text-gray-500">{formatWhen(row.lastSeenAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 mb-4">
        <label className="text-sm text-gray-700">
          Status
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="ml-2 border rounded px-2 py-1"
          >
            <option value="">Sve ({total})</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p className="text-gray-500">Učitavanje…</p>
      ) : reports.length === 0 ? (
        <p className="text-gray-500">Još nema prijava.</p>
      ) : (
        <div className="space-y-3">
          {reports.map((report) => (
            <article key={report._id} className="bg-white border rounded-lg p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-gray-900">
                    {APPLIANCE_LABELS[report.appliance] || report.appliance}
                    {report.name ? ` · ${report.name}` : ''}
                  </p>
                  <p className="text-sm text-gray-600">{report.address}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatWhen(report.createdAt)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={report.status}
                    onChange={(event) => changeStatus(report._id, event.target.value)}
                    className="border rounded px-2 py-1 text-sm"
                  >
                    {Object.entries(STATUS_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => (editingId === report._id ? setEditingId(null) : openEdit(report))}
                    className="p-2 rounded border text-gray-700 hover:bg-gray-50"
                    title="Izmeni"
                  >
                    {editingId === report._id ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(report)}
                    className="p-2 rounded border text-red-700 hover:bg-red-50"
                    title="Obriši"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm text-gray-800">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={Boolean(report.offerSent)}
                    onChange={(event) => toggleFlag(report, 'offerSent', event.target.checked)}
                  />
                  Ponuda poslata (mejl)
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={Boolean(report.called)}
                    onChange={(event) => toggleFlag(report, 'called', event.target.checked)}
                  />
                  Pozvan
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={report.status === 'done'}
                    onChange={(event) => toggleRepaired(report, event.target.checked)}
                  />
                  Popravka urađena
                </label>
              </div>

              {report.quoteAmount ? (
                <p className="mt-2 text-sm text-gray-700">Ponuda: {report.quoteAmount}</p>
              ) : null}
              {report.forwardedTo ? (
                <p className="mt-1 text-sm text-gray-700">Prosleđeno: {report.forwardedTo}</p>
              ) : null}
              {report.notes ? (
                <p className="mt-2 text-sm text-gray-600 whitespace-pre-wrap">Beleška: {report.notes}</p>
              ) : null}

              <p className="mt-3 text-gray-800 whitespace-pre-wrap">{report.description || 'Bez opisa'}</p>
              {report.machineModel ? (
                <p className="mt-2 text-sm text-gray-600">Model: {report.machineModel}</p>
              ) : null}
              {Array.isArray(report.imageUrls) && report.imageUrls.length ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {report.imageUrls.map((url) => (
                    <a key={url} href={url} target="_blank" rel="noreferrer">
                      <img src={url} alt="" className="h-20 w-20 object-cover rounded border" />
                    </a>
                  ))}
                </div>
              ) : null}

              <div className="mt-3 flex flex-wrap items-center gap-3">
                {report.email ? (
                  <>
                    <a
                      href={offerDraft(report).gmail}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => markOfferSent(report)}
                      className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-green-700"
                    >
                      <Mail className="h-4 w-4" />
                      Pošalji ponudu (Gmail)
                    </a>
                    <a
                      href={offerDraft(report).mailto}
                      className="text-sm text-gray-600 hover:underline"
                    >
                      Mail program
                    </a>
                    <a
                      href={offerDraft(report).gmail}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => markOfferSent(report)}
                      className="inline-flex items-center gap-2 text-green-700 font-medium hover:underline"
                    >
                      {report.email}
                    </a>
                  </>
                ) : null}
                {report.phone ? (
                  <a
                    href={`tel:${report.phone}`}
                    onClick={() => {
                      if (!report.called) toggleFlag(report, 'called', true);
                    }}
                    className="inline-flex items-center gap-2 text-green-700 font-medium"
                  >
                    <Phone className="h-4 w-4" />
                    {report.phone}
                  </a>
                ) : (
                  <span className="text-sm text-gray-400">Bez telefona</span>
                )}
              </div>
              {(report.utmSource || report.utmCampaign) && (
                <p className="mt-2 text-xs text-gray-400">
                  {[report.utmSource, report.utmMedium, report.utmCampaign].filter(Boolean).join(' · ')}
                </p>
              )}

              {editingId === report._id ? (
                <form onSubmit={saveEdit} className="mt-4 border-t pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="text-sm text-gray-700">
                    Ime
                    <input
                      className="mt-1 w-full border rounded px-3 py-2"
                      value={editForm.name}
                      onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                    />
                  </label>
                  <label className="text-sm text-gray-700">
                    Mejl
                    <input
                      type="email"
                      required
                      className="mt-1 w-full border rounded px-3 py-2"
                      value={editForm.email}
                      onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                    />
                  </label>
                  <label className="text-sm text-gray-700">
                    Telefon
                    <input
                      required
                      className="mt-1 w-full border rounded px-3 py-2"
                      value={editForm.phone}
                      onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                    />
                  </label>
                  <label className="text-sm text-gray-700">
                    Adresa
                    <input
                      required
                      className="mt-1 w-full border rounded px-3 py-2"
                      value={editForm.address}
                      onChange={(e) => setEditForm((f) => ({ ...f, address: e.target.value }))}
                    />
                  </label>
                  <label className="text-sm text-gray-700">
                    Uređaj
                    <select
                      className="mt-1 w-full border rounded px-3 py-2"
                      value={editForm.appliance}
                      onChange={(e) => setEditForm((f) => ({ ...f, appliance: e.target.value }))}
                    >
                      {Object.entries(APPLIANCE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-sm text-gray-700">
                    Model
                    <input
                      className="mt-1 w-full border rounded px-3 py-2"
                      value={editForm.machineModel}
                      onChange={(e) => setEditForm((f) => ({ ...f, machineModel: e.target.value }))}
                    />
                  </label>
                  <label className="text-sm text-gray-700 sm:col-span-2">
                    Opis kvara
                    <textarea
                      rows={3}
                      className="mt-1 w-full border rounded px-3 py-2"
                      value={editForm.description}
                      onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                    />
                  </label>
                  <label className="text-sm text-gray-700">
                    Iznos ponude
                    <input
                      className="mt-1 w-full border rounded px-3 py-2"
                      placeholder="npr. 4500 RSD"
                      value={editForm.quoteAmount}
                      onChange={(e) => setEditForm((f) => ({ ...f, quoteAmount: e.target.value }))}
                    />
                  </label>
                  <label className="text-sm text-gray-700">
                    Prosleđeno kolegi
                    <input
                      className="mt-1 w-full border rounded px-3 py-2"
                      placeholder="ime servisera"
                      value={editForm.forwardedTo}
                      onChange={(e) => setEditForm((f) => ({ ...f, forwardedTo: e.target.value }))}
                    />
                  </label>
                  <label className="text-sm text-gray-700 sm:col-span-2">
                    Beleška
                    <textarea
                      rows={2}
                      className="mt-1 w-full border rounded px-3 py-2"
                      placeholder="provizija, delovi, dogovor…"
                      value={editForm.notes}
                      onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                    />
                  </label>
                  <div className="sm:col-span-2 flex gap-2">
                    <button
                      type="submit"
                      disabled={saving}
                      className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-green-700 disabled:opacity-60"
                    >
                      {saving ? 'Čuvam…' : 'Sačuvaj'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="border px-4 py-2 rounded-md text-sm"
                    >
                      Otkaži
                    </button>
                  </div>
                </form>
              ) : null}
            </article>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => load(page - 1)}
            className="p-2 disabled:opacity-40"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="text-sm">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => load(page + 1)}
            className="p-2 disabled:opacity-40"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default FaultReportsView;
